import { onCLS, onINP, onFCP, onLCP, onTTFB } from 'web-vitals';

// Performance monitoring configuration
const vitalsConfig = {
  // Thresholds for good/needs improvement/poor
  thresholds: {
    CLS: { good: 0.1, poor: 0.25 },
    INP: { good: 200, poor: 500 },
    FCP: { good: 1800, poor: 3000 },
    LCP: { good: 2500, poor: 4000 },
    TTFB: { good: 800, poor: 1800 },
  },
};

// Send metrics to analytics or monitoring service
const sendToAnalytics = (metric) => {
  const { name, value, rating } = metric;
  
  // Log in development
  if (process.env.NODE_ENV === 'development') {
    console.log(`[Performance] ${name}: ${value.toFixed(2)}ms (${rating})`);
  }
  
  // Send to Google Analytics if configured
  if (window.gtag && process.env.REACT_APP_GA_TRACKING_ID) {
    window.gtag('event', name, {
      value: Math.round(value),
      metric_rating: rating,
      non_interaction: true,
    });
  }
  
  // Send to custom analytics endpoint
  if (process.env.REACT_APP_ENABLE_ANALYTICS === 'true') {
    // Batch metrics and send periodically
    performanceBuffer.add(metric);
  }
};

// Performance metrics buffer
const performanceBuffer = {
  metrics: [],
  
  add(metric) {
    this.metrics.push({
      name: metric.name,
      value: metric.value,
      rating: metric.rating,
      timestamp: Date.now(),
    });
    
    // Send when buffer reaches 10 metrics
    if (this.metrics.length >= 10) {
      this.flush();
    }
  },
  
  async flush() {
    if (this.metrics.length === 0) return;
    
    const metrics = [...this.metrics];
    this.metrics = [];
    
    // Performance metrics collection is disabled for now
    // To enable, set REACT_APP_PERFORMANCE_ENDPOINT in environment
    if (process.env.REACT_APP_PERFORMANCE_ENDPOINT) {
      try {
        await fetch(process.env.REACT_APP_PERFORMANCE_ENDPOINT, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            metrics,
            url: window.location.href,
            userAgent: navigator.userAgent,
          }),
        });
      } catch (error) {
        console.error('Failed to send performance metrics:', error);
      }
    }
  },
};

// Initialize web vitals monitoring
export function initPerformanceMonitoring() {
  // Core Web Vitals
  onCLS(sendToAnalytics);
  onINP(sendToAnalytics);
  onFCP(sendToAnalytics);
  onLCP(sendToAnalytics);
  onTTFB(sendToAnalytics);
  
  // Custom performance marks
  measureAppStartup();
  
  // Flush metrics on page unload
  window.addEventListener('beforeunload', () => {
    performanceBuffer.flush();
  });
}

// Measure app startup time
function measureAppStartup() {
  // Wait for app to be fully loaded
  window.addEventListener('load', () => {
    setTimeout(() => {
      const navEntry = performance.getEntriesByType('navigation')[0];
      
      if (navEntry) {
        const startupTime = navEntry.loadEventEnd - navEntry.fetchStart;
        
        sendToAnalytics({
          name: 'app_startup',
          value: startupTime,
          rating: startupTime < 3000 ? 'good' : startupTime < 5000 ? 'needs-improvement' : 'poor',
        });
      }
    }, 0);
  });
}

// Performance observer for long tasks
export function observeLongTasks() {
  if (!('PerformanceObserver' in window)) return;
  
  try {
    const observer = new PerformanceObserver((list) => {
      for (const entry of list.getEntries()) {
        if (entry.duration > 50) {
          console.warn(`Long task detected: ${entry.duration.toFixed(2)}ms`);
          
          sendToAnalytics({
            name: 'long_task',
            value: entry.duration,
            rating: entry.duration < 100 ? 'good' : entry.duration < 200 ? 'needs-improvement' : 'poor',
          });
        }
      }
    });
    
    observer.observe({ entryTypes: ['longtask'] });
  } catch (error) {
    console.error('Failed to observe long tasks:', error);
  }
}

// Measure component render time
export function measureComponentPerformance(componentName) {
  const startMark = `${componentName}-start`;
  const endMark = `${componentName}-end`;
  const measureName = `${componentName}-render`;
  
  return {
    start() {
      performance.mark(startMark);
    },
    
    end() {
      performance.mark(endMark);
      performance.measure(measureName, startMark, endMark);
      
      const measure = performance.getEntriesByName(measureName)[0];
      
      if (measure && measure.duration > 16) {
        console.warn(`Slow render: ${componentName} took ${measure.duration.toFixed(2)}ms`);
      }
      
      // Clean up marks
      performance.clearMarks(startMark);
      performance.clearMarks(endMark);
      performance.clearMeasures(measureName);
    },
  };
}

// React component performance profiler
export function ProfilerOnRender(id, phase, actualDuration, baseDuration, startTime, commitTime) {
  if (actualDuration > 16) {
    console.warn(`Slow ${phase}: ${id} took ${actualDuration.toFixed(2)}ms`);
    
    sendToAnalytics({
      name: `react_render_${phase}`,
      value: actualDuration,
      rating: actualDuration < 16 ? 'good' : actualDuration < 50 ? 'needs-improvement' : 'poor',
    });
  }
}